import React, { useState } from "react";
import Swal from "sweetalert2";
import moment from "moment";
import Teachr from "../../../Objects/models/Teachr";
import TravelRepository, {
    SCENARIO_APPROVAL,
    SCENARIO_ATTESTATION,
    SCENARIO_SAME_ADDRESS,
    TransportSubscriptionSummary,
    TravelInfo,
    modeUsesSubscription,
} from "../../../Repositories/TravelRepository";
import TransportSubscriptionRepository from "../../../Repositories/TransportSubscriptionRepository";
import TravelStore from "./TravelStore";
import AttestationModal from "./AttestationModal";
import UploadPreuveModal from "./UploadPreuveModal";
import TransportSubscriptionUpload from "./TransportSubscriptionUpload";
import TravelModeQuestion from "./TravelModeQuestion";
import { fullNameChild } from "../../../Utils/Function";
import { BusinessError } from "../../../Objects/exceptions";
import {
    ENV_DECLARONS,
    getActiveClientBrandConfig,
    getActivePrestationBrandConfig,
    isBrandActive,
} from "../../../config/environment.config";

type TravelModalState = {
    modal: "attestation" | "proof" | "subscription" | "mode" | null;
    info: TravelInfo | null;
    clientId: number | null;
    clientName: string;
    onProceed: (() => void) | null;
    /** P6 : période du cours en cours de déclaration, pour valider un abonnement. */
    coursePeriod?: CoursePeriod;
    /** P7 : suite du scénario à jouer une fois la question du mode de déplacement traitée. */
    afterMode?: ((info: TravelInfo | null) => void) | null;
};

const initialState: TravelModalState = {
    modal: null,
    info: null,
    clientId: null,
    clientName: "",
    onProceed: null,
    coursePeriod: undefined,
    afterMode: null,
};

/** Période du cours (dates finales), connue au récapitulatif et à l'édition. */
export type CoursePeriod = {
    start: Date | string | null | undefined;
    end: Date | string | null | undefined;
};

/**
 * P6 : l'abonnement ne fait foi que s'il couvre TOUTE la période du cours.
 * Période inconnue (début de flux, dates pas encore choisies) : on laisse
 * passer, la vérification définitive a lieu quand les dates sont posées.
 */
const subscriptionCoversPeriod = (
    subscription: TransportSubscriptionSummary | null | undefined,
    period?: CoursePeriod,
    ignoreConfirmation = false
): boolean => {
    if (!subscription) return false;
    // Adresse modifiée : l'abonnement ne vaut pas preuve avant confirmation.
    if (subscription.needsConfirmation && !ignoreConfirmation) return false;
    if (!period?.start || !period?.end) return true;
    const from = moment(subscription.validFrom, "YYYY-MM-DD").startOf("day");
    const until = moment(subscription.validUntil, "YYYY-MM-DD").endOf("day");
    return moment(period.start).isSameOrAfter(from) && moment(period.end).isSameOrBefore(until);
};

/** « du 01/10/2026 au 31/10/2026 » */
const formatPeriod = (start: any, end: any): string =>
    `du ${moment(start).format("DD/MM/YYYY")} au ${moment(end).format("DD/MM/YYYY")}`;

/**
 * TravelModeSelector : au démarrage du flux de déclaration, interroge le
 * backend (TravelDistanceService) et branche sur le bon scénario :
 * - < 60 km : aucune action, le flux continue ;
 * - 60–160 km : AttestationModal si aucune attestation valide n'existe ;
 * - > 160 km : UploadPreuveModal (preuve obligatoire, cours en attente admin).
 *
 * Ne bloque jamais une déclaration légitime : une erreur réseau ou serveur
 * laisse le flux continuer. Seul un refus d'accès (client non rattaché)
 * arrête la déclaration, puisque le serveur la refuserait de toute façon.
 */
export const useTravelScenario = (teachr: Teachr) => {
    const [state, setState] = useState<TravelModalState>(initialState);
    const [travelLoading, setTravelLoading] = useState(false);
    
    const closeModal = () => setState(initialState);

    const checkTravelScenario = async (child: any, onProceed: () => void, coursePeriod?: CoursePeriod) => {
        const clientId = child?.clientId;
        if (!teachr?.id || !clientId) {
            onProceed();
            return;
        }
        setTravelLoading(true);
        let info: TravelInfo | null = null;
        try {
            // P6 : la période du cours est transmise pour que le backend
            // renvoie l'abonnement qui la couvre, et non celui du jour même.
            info = await TravelRepository.getTravelInfo(teachr.id, clientId, child?.addressId ?? null, {
                startDate: coursePeriod?.start ? moment(coursePeriod.start).format("YYYY-MM-DD") : undefined,
                endDate: coursePeriod?.end ? moment(coursePeriod.end).format("YYYY-MM-DD") : undefined,
            });
            TravelStore.setInfo(clientId, info);
        } catch (e) {
            setTravelLoading(false);
            // Une erreur 4xx de travel-info signifie que le client n'est pas
            // rattaché au prof (403) ou n'existe plus (404) : la déclaration
            // serait refusée par le serveur, on ne la laisse pas partir.
            if (e instanceof BusinessError) {
                await Swal.fire({
                    icon: "error",
                    title: "Déclaration impossible",
                    html: `${getActiveClientBrandConfig("", "Cet ", "Ce ")} n'est pas rattaché à votre compte.<br/>Ajoutez-le à votre liste avant de déclarer ${getActivePrestationBrandConfig("", "un ", "une ")}.`,
                    confirmButtonText: "J'ai compris",
                });
                return;
            }
            // Erreur réseau ou serveur : ne jamais bloquer une déclaration légitime.
            console.warn("Travel info indisponible, déclaration normale", e);
        }
        setTravelLoading(false);

        const clientName = fullNameChild(child?.childFirstName, child?.childLastName);
        const runScenario = async (info: TravelInfo | null) => {
            if (info?.scenario === SCENARIO_SAME_ADDRESS) {
                // Adresse prof/client identique : pas de pièce à fournir, mais la
                // déclaration partira en approbation admin : on prévient une fois.
                if (!TravelStore.wasSameAddressAcknowledged(clientId)) {
                    const fem = isBrandActive(ENV_DECLARONS) ? "e" : "";
                    await Swal.fire({
                        icon: "info",
                        title: "Validation requise",
                        html: `Votre adresse est identique à celle de votre ${getActiveClientBrandConfig()}.<br/>Votre ${getActivePrestationBrandConfig()} sera enregistré${fem} <b>en attente de validation par notre équipe</b> avant d'être déclaré${fem}.`,
                        confirmButtonText: "J'ai compris",
                    });
                    TravelStore.acknowledgeSameAddress(clientId);
                }
                onProceed();
                return;
            }
            if (info?.scenario === SCENARIO_ATTESTATION && !info.attestation) {
                setState({ modal: "attestation", info, clientId, clientName, onProceed });
                return;
            }
            if (info?.scenario === SCENARIO_APPROVAL && !TravelStore.getProofFile(clientId)) {
                // P6 : l'abonnement renvoyé par le backend couvre déjà la période
                // demandée ; la vérification locale reste un garde-fou quand les
                // dates n'étaient pas connues au moment de l'appel.
                if (subscriptionCoversPeriod(info.transportSubscription, coursePeriod)) {
                    TravelStore.setUseSubscription(clientId, true);
                    onProceed();
                    return;
                }
                TravelStore.setUseSubscription(clientId, false);

                // Adresse modifiée (la sienne ou celle du client) : l'abonnement
                // du couple couvre la période mais attend la confirmation du prof.
                if (info?.transportSubscriptionReason === "to_confirm") {
                    const pending = (info.transportSubscriptions ?? []).find(
                        (sub) => sub.needsConfirmation && subscriptionCoversPeriod(sub, coursePeriod, true)
                    );
                    if (pending) {
                        const result = await Swal.fire({
                            icon: "warning",
                            title: "Abonnement à confirmer",
                            html: `${pending.confirmationReason || "Une adresse a été modifiée"}.<br/>Votre abonnement <b>${pending.typeLabel}</b> (valide ${formatPeriod(pending.validFrom, pending.validUntil)}) couvre-t-il toujours vos trajets jusqu'à <b>${clientName}</b> ?`,
                            showDenyButton: true,
                            showCancelButton: true,
                            confirmButtonText: "Je confirme",
                            denyButtonText: "Joindre un billet",
                            cancelButtonText: "Annuler",
                        });
                        if (result.isConfirmed) {
                            try {
                                await TransportSubscriptionRepository.confirm(pending.id);
                                const confirmed = { ...pending, needsConfirmation: false, confirmationReason: null };
                                TravelStore.patchInfo(clientId, { transportSubscription: confirmed, transportSubscriptionReason: null });
                                TravelStore.setUseSubscription(clientId, true);
                                onProceed();
                            } catch (e) {
                                console.warn("Confirmation de l'abonnement impossible", e);
                                await Swal.fire({ icon: "error", title: "Confirmation impossible", text: "Réessayez plus tard, ou joignez un billet pour cette déclaration.", confirmButtonText: "J'ai compris" });
                                setState({ modal: "proof", info, clientId, clientName, onProceed, coursePeriod });
                            }
                        } else if (result.isDenied) {
                            setState({ modal: "proof", info, clientId, clientName, onProceed, coursePeriod });
                        }
                        return;
                    }
                }

                // Des abonnements existent mais aucun ne couvre la période du
                // cours : on l'explique au lieu de redemander une preuve sèchement.
                const others = (info?.transportSubscriptions ?? []).filter(
                    (sub) => !subscriptionCoversPeriod(sub, coursePeriod)
                );
                if (others.length > 0 && coursePeriod?.start && coursePeriod?.end) {
                    const list = others
                        .map((sub) => `<li>${sub.typeLabel}, valide ${formatPeriod(sub.validFrom, sub.validUntil)}</li>`)
                        .join("");
                    const result = await Swal.fire({
                        icon: "warning",
                        title: "Abonnement hors période",
                        html: `Votre ${getActivePrestationBrandConfig()} va <b>${formatPeriod(coursePeriod.start, coursePeriod.end)}</b>, mais ${others.length > 1 ? "aucun de vos abonnements ne couvre" : "votre abonnement ne couvre pas"} cette période :<ul style="text-align:left;display:inline-block;margin-top:8px">${list}</ul><br/>Ajustez les dates ${getActivePrestationBrandConfig("", "du ", "de la ")}, déposez l'abonnement correspondant, ou joignez une preuve ponctuelle.`,
                        showDenyButton: true,
                        showCancelButton: true,
                        confirmButtonText: "Déposer un abonnement",
                        denyButtonText: "Joindre une preuve",
                        cancelButtonText: "Modifier les dates",
                    });
                    if (result.isConfirmed) {
                        setState({ modal: "subscription", info, clientId, clientName, onProceed, coursePeriod });
                    } else if (result.isDenied) {
                        setState({ modal: "proof", info, clientId, clientName, onProceed, coursePeriod });
                    }
                    // « Modifier les dates » : on n'avance pas, le prof ajuste.
                    return;
                }

                // Des abonnements existent, mais rattachés à d'autres clients :
                // on le dit, et on propose d'en déposer un pour celui-ci.
                if (info?.transportSubscriptionReason === "other_client") {
                    const result = await Swal.fire({
                        icon: "warning",
                        title: "Abonnement rattaché à un autre client",
                        html: `Vos abonnements de transport sont rattachés à d'autres ${getActiveClientBrandConfig("s")} : ils ne valent pas preuve pour <b>${clientName}</b>.<br/>Déposez l'abonnement correspondant à ce trajet, ou joignez une preuve ponctuelle.`,
                        showDenyButton: true,
                        showCancelButton: true,
                        confirmButtonText: "Déposer un abonnement",
                        denyButtonText: "Joindre une preuve",
                        cancelButtonText: "Annuler",
                    });
                    if (result.isConfirmed) {
                        setState({ modal: "subscription", info, clientId, clientName, onProceed, coursePeriod });
                    } else if (result.isDenied) {
                        setState({ modal: "proof", info, clientId, clientName, onProceed, coursePeriod });
                    }
                    return;
                }

                setState({ modal: "proof", info, clientId, clientName, onProceed, coursePeriod });
                return;
            }
            onProceed();
        };

        // P7 : au premier cours pour un client sans mode de déplacement connu,
        // question légère et ignorable. Pas entre 60 et 160 km (l'attestation
        // demande déjà le mode) ni à adresse identique.
        const modeUnknown = !!info && !info.travelMode
            && info.scenario !== SCENARIO_ATTESTATION
            && info.scenario !== SCENARIO_SAME_ADDRESS
            && !TravelStore.wasModeQuestionSkipped(teachr?.id, clientId);
        if (modeUnknown) {
            setState({ modal: "mode", info, clientId, clientName, onProceed, coursePeriod, afterMode: runScenario });
            return;
        }
        await runScenario(info);
    };

    // P7 : le mode choisi est enregistré ; si la distance est incohérente avec
    // ce mode, l'anomalie est signalée tout de suite, sans bloquer.
    const handleModeSelected = async (mode: string) => {
        const { clientId, afterMode } = state;
        let info = state.info;
        if (clientId != null && teachr?.id && info) {
            try {
                const result = await TravelRepository.setTravelMode(teachr.id, clientId, mode);
                const usesSubscription = modeUsesSubscription(mode);
                info = { ...info, travelMode: result.travelMode, complianceAlert: result.complianceAlert, travelModeUsesSubscription: usesSubscription };
                TravelStore.patchInfo(clientId, { travelMode: result.travelMode, complianceAlert: result.complianceAlert, travelModeUsesSubscription: usesSubscription });
                if (result.complianceAlert && result.complianceAlert.status === "open") {
                    const answer = await Swal.fire({
                        icon: "warning",
                        title: "Distance inhabituelle",
                        html: `${result.complianceAlert.message}<br/><span style="font-size:0.85em">La distance est estimée par le trajet routier. Votre ${getActivePrestationBrandConfig()} n'est pas bloqué${isBrandActive(ENV_DECLARONS) ? "e" : ""}.</span>`,
                        showCancelButton: true,
                        confirmButtonText: "Je confirme ce déplacement",
                        cancelButtonText: "Continuer sans confirmer",
                    });
                    if (answer.isConfirmed) {
                        try {
                            const confirmed = await TravelRepository.confirmComplianceFlag(result.complianceAlert.id);
                            info = { ...info, complianceAlert: confirmed };
                            TravelStore.patchInfo(clientId, { complianceAlert: confirmed });
                        } catch (e) {
                            console.warn("Confirmation du déplacement impossible", e);
                        }
                    }
                }
            } catch (e) {
                // jamais bloquant : le mode pourra être renseigné depuis la fiche client
                console.warn("Enregistrement du mode de déplacement impossible", e);
            }
        }

        // Transports en commun ou train : on propose tout de suite l'abonnement
        // (facultatif sous le seuil haut, requis au-delà, ou un billet par cours).
        if (info && clientId != null && modeUsesSubscription(mode) && !info.transportSubscription) {
            const required = info.scenario === SCENARIO_APPROVAL;
            const answer = await Swal.fire({
                icon: "question",
                title: `Vous voyagez en ${(info.travelModes?.[mode] ?? mode).toLowerCase()}`,
                html: required
                    ? `À plus de ${info.thresholdHigh} km, une preuve est demandée à chaque ${getActivePrestationBrandConfig()}. Renseignez votre abonnement une fois : il servira de preuve pour toutes vos déclarations avec <b>${state.clientName}</b>. Sinon, un billet vous sera demandé à chaque déclaration.`
                    : `Vous pouvez renseigner votre abonnement (Navigo, TER...) pour vos trajets jusqu'à <b>${state.clientName}</b>. C'est facultatif à cette distance.`,
                showCancelButton: true,
                confirmButtonText: "Renseigner mon abonnement",
                cancelButtonText: "Plus tard",
            });
            if (answer.isConfirmed) {
                // le dépôt reprend ensuite le scénario (afterMode) avec l'abonnement en place
                setState({ modal: "subscription", info, clientId, clientName: state.clientName, onProceed: null, coursePeriod: state.coursePeriod, afterMode });
                return;
            }
        }
        closeModal();
        afterMode && afterMode(info);
    };

    const handleModeSkipped = () => {
        const { clientId, afterMode, info } = state;
        TravelStore.markModeQuestionSkipped(teachr?.id, clientId);
        closeModal();
        afterMode && afterMode(info);
    };

    const travelModals = (
        <>
            {state.modal === "mode" && state.info && state.clientId != null && (
                <TravelModeQuestion
                    open
                    clientName={state.clientName}
                    travelModes={state.info.travelModes ?? {}}
                    onSelect={handleModeSelected}
                    onSkip={handleModeSkipped}
                />
            )}
            {state.modal === "attestation" && state.info && state.clientId != null && (
                <AttestationModal
                    open
                    teachr={teachr}
                    clientId={state.clientId}
                    clientName={state.clientName}
                    travelInfo={state.info}
                    onClose={closeModal}
                    onSigned={() => {
                        const proceed = state.onProceed;
                        closeModal();
                        proceed && proceed();
                    }}
                />
            )}
            {state.modal === "proof" && state.info && (
                <UploadPreuveModal
                    open
                    travelInfo={state.info}
                    ticketMode={!!state.info.travelModeUsesSubscription}
                    onClose={closeModal}
                    onConfirm={(file) => {
                        TravelStore.setProofFile(file);
                        const proceed = state.onProceed;
                        closeModal();
                        proceed && proceed();
                    }}
                    // Abonnement proposé seulement en transports en commun / train,
                    // ou quand le mode n'est pas connu : jamais en voiture ou à vélo.
                    onUseSubscription={(state.info.travelModeUsesSubscription || !state.info.travelMode) ? () => {
                        setState((prev) => ({ ...prev, modal: "subscription" }));
                    } : undefined}
                />
            )}
            {state.modal === "subscription" && state.clientId != null && (
                <TransportSubscriptionUpload
                    open
                    teachr={teachr}
                    clientId={state.clientId}
                    clientName={state.clientName}
                    coursePeriod={state.coursePeriod}
                    onClose={() => {
                        // Proposition après le choix du mode (« Plus tard ») :
                        // le scénario reprend sans abonnement.
                        if (state.onProceed === null && state.afterMode) {
                            const { afterMode, info } = state;
                            closeModal();
                            afterMode(info);
                            return;
                        }
                        setState((prev) => ({ ...prev, modal: "proof" }));
                    }}
                    onSaved={async (subscription) => {
                        // Proposition après le choix du mode : l'abonnement est
                        // en place, le scénario reprend avec lui.
                        if (state.onProceed === null && state.afterMode) {
                            const { afterMode, info, clientId } = state;
                            const nextInfo = info
                                ? { ...info, transportSubscription: subscription, transportSubscriptions: [subscription, ...(info.transportSubscriptions ?? [])], transportSubscriptionReason: null }
                                : info;
                            if (clientId != null && nextInfo) {
                                TravelStore.patchInfo(clientId, { transportSubscription: subscription, transportSubscriptionReason: null });
                            }
                            closeModal();
                            afterMode(nextInfo);
                            return;
                        }
                        // L'abonnement qui vient d'être déposé fait foi
                        // immédiatement : on le valide ici plutôt que de
                        // relancer un appel qui pourrait ne pas le retenir.
                        if (!subscriptionCoversPeriod(subscription, state.coursePeriod)) {
                            await Swal.fire({
                                icon: "warning",
                                title: "Abonnement hors période",
                                html: `Cet abonnement est valide ${formatPeriod(subscription.validFrom, subscription.validUntil)}, mais votre ${getActivePrestationBrandConfig()} va ${formatPeriod(state.coursePeriod?.start, state.coursePeriod?.end)}.<br/>Joignez une preuve ponctuelle ou ajustez les dates.`,
                                confirmButtonText: "J'ai compris",
                            });
                            setState((prev) => ({ ...prev, modal: "proof" }));
                            return;
                        }
                        TravelStore.setUseSubscription(state.clientId, true);
                        const proceed = state.onProceed;
                        closeModal();
                        proceed && proceed();
                    }}
                />
            )}
        </>
    );

    return { checkTravelScenario, travelModals, travelLoading };
};
